import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { GenericGet } from '@/_shared/types';
import { getCurrentOrganization } from '@/core/state/selectors';
import { API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_DEPARTMENTS_DEPARTMENTID_TEAMS_TEAMID_YEAR_YEAR_QUARTER_QUARTER_PATH_KEY } from '@/swagger/constants/credits-keys';
import { API_GET_DEPARTMENT_TEAM_CREDITS, KEY_GET_DEPARTMENT_TEAM_CREDITS } from '../constants';
import { MOCK_RESPONSE_GET_DEPARTMENT_TEAM_CREDITS } from '../mocks';

type RequestGetDepartmentTeamCredits = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_DEPARTMENTS_DEPARTMENTID_TEAMS_TEAMID_YEAR_YEAR_QUARTER_QUARTER_PATH_KEY
>['request']['path'];

export type ResponseGetDepartmentTeamCredits = GenericGet<
  typeof API_ORGANIZATIONS_ORGANIZATIONID_CREDITS_DEPARTMENTS_DEPARTMENTID_TEAMS_TEAMID_YEAR_YEAR_QUARTER_QUARTER_PATH_KEY
>['response']['body'];

export type CallParamsGetDepartmentTeamCredits = Omit<
  RequestGetDepartmentTeamCredits,
  'organizationId'
> & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

type CallResponseGetDepartmentTeamCredits = ResponseGetDepartmentTeamCredits;

type CallErrorGetDepartmentTeamCredits = {
  message: string;
  code: number;
};

type CallSelectorsGetDepartmentTeamCredits = {
  stats: CallResponseGetDepartmentTeamCredits['stats'];
  clusterUsers: CallResponseGetDepartmentTeamCredits['stats']['clusterUsers'];
  hasNoData: boolean;
};

export const getDepartmentTeamCreditsCall = createGeneralCall<
  CallParamsGetDepartmentTeamCredits,
  CallResponseGetDepartmentTeamCredits,
  CallErrorGetDepartmentTeamCredits,
  CallSelectorsGetDepartmentTeamCredits
>({
  id: KEY_GET_DEPARTMENT_TEAM_CREDITS,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_DEPARTMENT_TEAM_CREDITS(
        org as unknown as string,
        payload.departmentId,
        payload.teamId,
        payload.year,
        payload.quarter,
      );
    },
  paramsSelector:
    ({ payload }) =>
    () => {
      return payload;
    },
  method: 'get',
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_DEPARTMENT_TEAM_CREDITS : undefined,
  selectors: {
    stats: ({ selectors }) => createSelector(selectors.response, res => res?.stats),
    clusterUsers: ({ selectors }) =>
      createSelector(selectors.response, res => res?.stats?.clusterUsers || []),
    hasNoData: ({ selectors }) =>
      createSelector(
        selectors.response,
        res => res?.stats?.assigned === 0 && res?.stats?.clusterUsers?.length === 0,
      ),
  },
});
